import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchTyre, fetchRelated } from "../lib/api";
import Stars from "../components/Stars";
import ReviewsSection from "../components/ReviewsSection";
import Seo from "../components/Seo";
import { useCart } from "../context/CartContext";
import { ChevronRight, Truck, Wrench, ShoppingCart, Droplet, Volume2, Fuel, ShieldCheck, Lock } from "lucide-react";

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-4 my-2">
    <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
    <h2 className="px-2 whitespace-nowrap" style={{ fontSize: 32 }}>{children}</h2>
    <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
  </div>
);

const TyreDetail = () => {
  const { id } = useParams();
  const [tyre, setTyre] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const { addItem } = useCart();

  useEffect(() => {
    fetchTyre(id).then((t) => { setTyre(t); setActiveImg(0); });
    fetchRelated(id).then(setRelated).catch(() => setRelated([]));
    // analytics: fire a 'view' event (fire & forget — no auth required)
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/analytics/event`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "view", product_id: id }),
      }).catch(() => {});
    } catch (_) { /* swallow */ }
  }, [id]);

  if (!tyre) return <div className="vd-container py-20"><p className="vd-muted">Loading tyre...</p></div>;

  const sizeStr = `${tyre.size.width}/${tyre.size.profile} R${tyre.size.rim}`;
  const images = tyre.gallery && tyre.gallery.length ? tyre.gallery : [tyre.image];

  return (
    <div className="vd-page" data-testid="page-tyre-detail">
      <Seo
        title={`${tyre.brand} ${tyre.model} ${sizeStr}`}
        description={`Buy ${tyre.brand} ${tyre.model} ${sizeStr} ${tyre.season} tyres online — £${tyre.price.toFixed(2)} with free UK delivery on 2+ tyres, EU label ${tyre.fuel_efficiency}/${tyre.wet_grip}, mobile fitting available.`}
        path={`/tyres/${tyre.id}`}
        image={tyre.image?.startsWith("http") ? tyre.image : undefined}
      />
      {/* Breadcrumbs */}
      <div className="vd-container py-4 flex items-center gap-2 text-sm" data-testid="breadcrumbs">
        <Link to="/" className="vd-muted hover:text-[var(--primary-red)]">Home</Link>
        <ChevronRight size={14} color="var(--text-muted)" />
        <Link to="/tyres" className="vd-muted hover:text-[var(--primary-red)]">Shop Tyres</Link>
        <ChevronRight size={14} color="var(--text-muted)" />
        <span className="vd-heading font-semibold">{tyre.brand} {tyre.model}</span>
      </div>

      {/* Centered title with horizontal lines */}
      <div className="vd-container pt-2 pb-4">
        <SectionTitle>{tyre.brand} {tyre.model} {sizeStr}</SectionTitle>
      </div>

      {/* 3-column hero: price+thumbs LEFT, big image CENTER, details panel RIGHT */}
      <section className="vd-container pb-6 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_1fr] gap-8 items-start">
        {/* LEFT: price, rating, CTAs, thumbnails */}
        <div>
          <p className="font-extrabold mb-2" style={{ color: "var(--primary-red)", fontSize: 44 }} data-testid="tyre-price">£{tyre.price.toFixed(2)}</p>
          <div className="mb-3"><Stars value={tyre.rating} size={20} /></div>

          {/* Trust signals — additive, no layout change */}
          <ul className="flex flex-col gap-1.5 mb-5 text-sm" data-testid="tyre-trust-signals">
            <li className="flex items-center gap-2 vd-body"><Truck size={14} color="var(--primary-red)" /> Free UK delivery on 2+ tyres</li>
            <li className="flex items-center gap-2 vd-body"><ShieldCheck size={14} color="var(--primary-red)" /> {tyre.warranty} manufacturer warranty</li>
            <li className="flex items-center gap-2 vd-body"><Lock size={14} color="var(--primary-red)" /> Secure SSL checkout</li>
          </ul>

          <div className="flex flex-col gap-3 mb-6 max-w-xs">
            <button
              onClick={() => addItem({ tyre_id: tyre.id, name: `${tyre.brand} ${tyre.model} ${sizeStr}`, price: tyre.price, image: tyre.image }, qty)}
              className="vd-btn-primary justify-center"
              data-testid="add-to-basket-btn"
            >
              <ShoppingCart size={18} /> Add to Basket
            </button>
            <Link to="/mobile-fitting" className="vd-btn-secondary justify-center" data-testid="book-mobile-fitting-btn">Book Mobile Fitting</Link>
          </div>

          <div className="flex items-center gap-3 mb-5">
            <label className="vd-muted text-sm">Qty</label>
            <div className="inline-flex items-center border rounded-md" style={{ borderColor: "var(--border-color)" }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 vd-heading" data-testid="qty-decrement">−</button>
              <span className="px-4 vd-heading font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="px-3 py-2 vd-heading" data-testid="qty-increment">+</button>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="grid grid-cols-3 gap-3">
            {images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActiveImg(i)}
                className="rounded-md overflow-hidden aspect-square"
                style={{ border: activeImg === i ? "2px solid var(--primary-red)" : "1px solid var(--border-color)", background: "var(--bg-secondary)" }}
                data-testid={`thumbnail-${i}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* CENTER: large product image */}
        <div className="vd-card overflow-hidden flex items-center justify-center aspect-square relative" style={{ background: "var(--bg-secondary)" }}>
          <img src={images[activeImg]} alt={tyre.model} className="w-full h-full object-cover" data-testid="tyre-main-image" />
          {/* Red glow streak under image (matches mockup) */}
          <span className="vd-tyre-card-glow" aria-hidden="true" />
        </div>

        {/* RIGHT: Tyre Details + EU Rating panel */}
        <div className="flex flex-col gap-4">
          <div className="vd-card p-5" data-testid="tyre-details-panel">
            <h3 className="mb-4" style={{ fontSize: 20 }}>Tyre Details</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-3 text-sm">
              <dt className="vd-muted">Brand:</dt><dd className="vd-heading font-medium text-right">{tyre.brand}</dd>
              <dt className="vd-muted">Size:</dt><dd className="vd-heading font-medium text-right">{sizeStr}</dd>
              <dt className="vd-muted">Season:</dt><dd className="vd-heading font-medium text-right">{tyre.season}</dd>
              <dt className="vd-muted">Load Index:</dt><dd className="vd-heading font-medium text-right">{tyre.load_index}</dd>
              <dt className="vd-muted">Speed Rating:</dt><dd className="vd-heading font-medium text-right">{tyre.speed_rating} (270 km/h)</dd>
              <dt className="vd-muted">Warranty:</dt><dd className="vd-heading font-medium text-right">{tyre.warranty}</dd>
            </dl>
          </div>

          <div className="vd-card p-5" data-testid="eu-rating-panel">
            <h3 className="mb-4" style={{ fontSize: 20 }}>EU Tyre Rating</h3>
            <div className="grid grid-cols-3 gap-3 text-center">
              <EuBadge Icon={Droplet} label="Wet Grip" value={tyre.wet_grip} />
              <EuBadge Icon={Fuel} label="Fuel Efficiency" value={tyre.fuel_efficiency} green />
              <EuBadge Icon={Volume2} label="Noise Level" value={tyre.noise_level} />
            </div>
          </div>
        </div>
      </section>

      {/* Delivery + Fitting service row (matches mockup) */}
      <section className="vd-container py-8 border-t" style={{ borderColor: "var(--border-color)" }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center rounded-full" style={{ width: 44, height: 44, background: "rgba(230,0,0,0.08)" }}>
              <Truck size={22} color="var(--primary-red)" />
            </span>
            <p className="vd-heading text-lg font-medium">Next Day Delivery Available</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center rounded-full" style={{ width: 44, height: 44, background: "rgba(230,0,0,0.08)" }}>
              <Wrench size={22} color="var(--primary-red)" />
            </span>
            <p className="vd-heading text-lg font-medium">Professional Mobile Fitting Service in Middlesbrough</p>
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <div className="vd-container">
        <ReviewsSection tyreId={tyre.id} avgRating={tyre.rating} />
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="vd-container py-10">
          <SectionTitle>Related Products</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {related.map((t) => <RelatedCard key={t.id} tyre={t} />)}
          </div>
        </section>
      )}
    </div>
  );
};

const EuBadge = ({ Icon, label, value, green = false }) => (
  <div className="flex flex-col items-center gap-2">
    <span
      className="inline-flex items-center justify-center rounded-md font-extrabold"
      style={{
        width: 44, height: 44,
        background: green ? "rgba(0, 180, 80, 0.18)" : "var(--bg-secondary)",
        color: green ? "#00C26E" : "var(--text-heading)",
        border: green ? "1px solid rgba(0, 180, 80, 0.4)" : "1px solid var(--border-color)",
        fontSize: 18,
      }}
    >
      {green ? value : <Icon size={20} color="var(--primary-red)" />}
    </span>
    {!green && <span className="vd-heading text-xs font-bold">{value}</span>}
    <span className="vd-muted text-xs">{label}</span>
  </div>
);

const RelatedCard = ({ tyre }) => {
  const sizeStr = `${tyre.size.width}/${tyre.size.profile} R${tyre.size.rim}`;
  return (
    <Link to={`/tyres/${tyre.id}`} className="vd-card overflow-hidden flex flex-col group" data-testid={`related-${tyre.id}`}>
      <div className="aspect-[4/3] overflow-hidden relative" style={{ background: "var(--bg-secondary)" }}>
        <img src={tyre.image} alt={tyre.model} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
        <span className="vd-tyre-card-glow" aria-hidden="true" />
      </div>
      <div className="p-5 flex flex-col flex-1">
        <p className="vd-muted text-xs uppercase tracking-wider">{tyre.brand}</p>
        <h4 className="vd-heading font-semibold mt-1" style={{ fontSize: 16 }}>{tyre.model}</h4>
        <p className="vd-muted text-sm mt-1">{sizeStr} · {tyre.season}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="font-extrabold" style={{ color: "var(--primary-red)", fontSize: 20 }}>£{tyre.price.toFixed(2)}</span>
          <span className="text-xs font-bold rounded px-2 py-1" style={{ background: "var(--primary-red)", color: "#fff" }}>VIEW</span>
        </div>
      </div>
    </Link>
  );
};

export default TyreDetail;
